import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Modal, Popconfirm, Row, Space, Table, message } from "antd";
import { booleanToYesNo, camelToWords, populateKey } from "common/utils";
import {
  useFetchAllDebtorAssetCustomFieldsQuery,
  useDeleteDebtorAssetCustomFieldMutation,
  useLazyFetchDebtorAssetCustomFieldQuery,
} from "features/userDefinedFields/debtorAssetCustomFieldsAPI";
import { useFetchBackendConstantsQuery } from "features/home/agencyPortal/homeAPI";
import {
  useDeleteAccountUdfCustomFieldMutation,
  useLazyFetchAccountUdfCustomFieldQuery,
} from "features/userDefinedFields/agencyPortal/accountUserDefinedFieldsAPI";
import { useFetchAllAccountUdfCustomFieldsQuery } from "features/userDefinedFields/accountUserDefinedFieldsAPI";
import {
  useDeleteCreditorUdfCustomFieldMutation,
  useFetchAllCreditorUdfCustomFieldsQuery,
  useLazyFetchCreditorUdfCustomFieldQuery,
} from "features/userDefinedFields/agencyPortal/creditorUserDefinedFieldsAPI";
import AddUserDefinedFieldModal from "features/userDefinedFields/components/addUserDefinedFieldModal";
import {
  useDeleteDebtorUdfCustomFieldMutation,
  useFetchAllDebtorUdfCustomFieldsQuery,
  useLazyFetchDebtorUdfCustomFieldQuery,
} from "features/userDefinedFields/agencyPortal/debtorUserDefinedFieldsAPI";
import {
  useFetchAllLegalCustomFieldsQuery,
  useDeleteLegalCustomFieldMutation,
  useLazyFetchLegalCustomFieldQuery,
} from "features/userDefinedFields/legalCustomFieldsAPI";
import { useMemo, useState } from "react";
import styled from "styled-components";
import {
  useDeleteUserUdfCustomFieldMutation,
  useFetchAllUserUdfCustomFieldsQuery,
  useLazyFetchUserUdfCustomFieldQuery,
} from "features/userDefinedFields/agencyPortal/userUserDefinedFieldsAPI";

const StyledRow = styled(Row)``;

const StyledHeader = styled.h4`
  text-transform: capitalize;
`;

export default function UserDefinedFieldsTable({ forType }) {
  const { data: constants } = useFetchBackendConstantsQuery();
  const { data: accountUdfCustomFields } = useFetchAllAccountUdfCustomFieldsQuery(
    {},
    { skip: forType !== "account" },
  );
  const { data: creditorUdfCustomFields } = useFetchAllCreditorUdfCustomFieldsQuery(
    {},
    { skip: forType !== "client" },
  );
  const { data: debtorUdfCustomFields } = useFetchAllDebtorUdfCustomFieldsQuery(
    {},
    { skip: forType !== "debtor" },
  );
  const { data: userUdfCustomFields } = useFetchAllUserUdfCustomFieldsQuery(
    {},
    { skip: forType !== "user" },
  );
  const { data: legalCustomFields } = useFetchAllLegalCustomFieldsQuery(
    {},
    { skip: forType !== "legal" },
  );
  const { data: debtorAssetCustomFields } = useFetchAllDebtorAssetCustomFieldsQuery(
    {},
    { skip: forType !== "debtorAsset" },
  );
  const [isModalVisible, setModalVisible] = useState(false);
  const [selectedUdf, setSelectedUdf] = useState(undefined);

  const [deleteAccountUdf] = useDeleteAccountUdfCustomFieldMutation();
  const [deleteDebtorUdf] = useDeleteDebtorUdfCustomFieldMutation();
  const [deleteCreditorUdf] = useDeleteCreditorUdfCustomFieldMutation();
  const [deleteUserUdf] = useDeleteUserUdfCustomFieldMutation();
  const [deleteLegalUdf] = useDeleteLegalCustomFieldMutation();
  const [deleteDebtorAssetUdf] = useDeleteDebtorAssetCustomFieldMutation();

  const [fetchAccountUdf] = useLazyFetchAccountUdfCustomFieldQuery();
  const [fetchDebtorUdf] = useLazyFetchDebtorUdfCustomFieldQuery();
  const [fetchCreditorUdf] = useLazyFetchCreditorUdfCustomFieldQuery();
  const [fetchUserUdf] = useLazyFetchUserUdfCustomFieldQuery();
  const [fetchLegalUdf] = useLazyFetchLegalCustomFieldQuery();
  const [fetchDebtorAssetUdf] = useLazyFetchDebtorAssetCustomFieldQuery();

  const deleteMutation = {
    account: deleteAccountUdf,
    client: deleteCreditorUdf,
    debtor: deleteDebtorUdf,
    debtorAsset: deleteDebtorAssetUdf,
    legal: deleteLegalUdf,
    user: deleteUserUdf,
  }[forType];

  const fetchDetail = {
    account: fetchAccountUdf,
    client: fetchCreditorUdf,
    debtor: fetchDebtorUdf,
    debtorAsset: fetchDebtorAssetUdf,
    legal: fetchLegalUdf,
    user: fetchUserUdf,
  }[forType];

  const udfCustomFields =
    {
      account: accountUdfCustomFields,
      client: creditorUdfCustomFields,
      debtor: debtorUdfCustomFields,
      debtorAsset: debtorAssetCustomFields,
      legal: legalCustomFields,
      user: userUdfCustomFields,
    }[forType] ?? [];

  const executeDelete = async (record) => {
    const result = await deleteMutation({ id: record.id });
    if ("data" in result) {
      message.success("Custom field deleted successfully.");
    }
  };

  const handleDeleteConfirm = async (record) => {
    const { data, error } = await fetchDetail({ id: record.id });
    if (error) {
      message.error("Unable to check custom field details. Please try again.");
      return;
    }
    const hasValues = data?.hasValues;

    if (hasValues) {
      Modal.confirm({
        title: "Delete Custom Field",
        content:
          "There are values associated with this custom field. Deleting it will also delete all values. Are you sure?",
        okText: "Yes, Delete",
        okType: "danger",
        onOk: () => executeDelete(record),
      });
    } else {
      await executeDelete(record);
    }
  };

  const onModalCancel = () => {
    setSelectedUdf(null);
    setModalVisible(false);
  };

  const onModalSubmit = () => {
    setSelectedUdf(null);
    setModalVisible(false);
  };

  const byCustomFieldTypeMap = useMemo(() => {
    return (constants?.customFieldTypes ?? []).reduce((acc, type) => {
      acc[type.value] = type;
      return acc;
    }, {});
  }, [constants]);

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Sort Key",
      dataIndex: "sortSlug",
      key: "sortSlug",
      render: (text) => text || "-",
    },
    {
      title: "Type",
      dataIndex: "type",
      render: (text) => byCustomFieldTypeMap[text]?.display,
      key: "type",
    },
    {
      title: "Is Required?",
      dataIndex: "isRequired",
      key: "isRequired",
      render: (text) => booleanToYesNo(text),
    },
    {
      title: "Is Important?",
      dataIndex: "isImportant",
      key: "isImportant",
      render: (text) => booleanToYesNo(text),
    },
    {
      title: "Actions",
      fixed: "right",
      dataIndex: "action",
      key: "action",
      render: (_, record) => (
        <Space size="middle">
          <EditOutlined
            key="edit"
            onClick={() => {
              setSelectedUdf(record);
              setModalVisible(true);
            }}
          />
          <Popconfirm
            placement="topLeft"
            okText="Yes"
            title="Are you sure you want to delete this custom field?"
            onConfirm={() => handleDeleteConfirm(record)}
          >
            <DeleteOutlined key="delete" />
          </Popconfirm>
        </Space>
      ),
    },
  ].filter((column) => !column.hidden);

  const addLinkCaption =
    {
      account: "Add Account Field",
      debtor: "Add Debtor Field",
      debtorAsset: "Add Debtor Asset Field",
      client: "Add Client Field",
      legal: "Add Legal Field",
      user: "Add User Field",
    }[forType] ?? "Add User Defined Field";

  return (
    <>
      <StyledRow align="middle">
        <StyledHeader>{camelToWords(forType)}</StyledHeader>
        <Button onClick={() => setModalVisible(true)} icon={<PlusOutlined />} type="link">
          {addLinkCaption}
        </Button>
      </StyledRow>
      <Table
        bordered
        scroll={{ x: "max-content" }}
        // @ts-ignore
        columns={columns}
        dataSource={populateKey(udfCustomFields)}
      />
      {isModalVisible && (
        <AddUserDefinedFieldModal
          onSubmit={onModalSubmit}
          onCancel={onModalCancel}
          userDefinedField={selectedUdf}
          forType={forType}
        />
      )}
    </>
  );
}
